import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { listApprovals, resolveApproval, approveAndExecute, rejectApproval, subscribe, isAhaComplete } from '../data/demoStore';
import {
  Shield, Clock, CheckCircle, XCircle, User, AlertTriangle, ArrowRight,
  Search, Filter, MoreHorizontal, Eye, MessageSquare, Zap, FileText,
  ChevronDown, History, Users, ExternalLink, Scale, BookOpen, Info,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip';
import { AsyncButton } from '../components/ui/async-button';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuCheckboxItem
} from '../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from '../components/ui/tabs';
import { cn } from '../lib/utils';

const mockApprovals = [
  {
    id: 'APR-001',
    type: 'access_request',
    title: 'Salesforce Access for Sarah Chen',
    description: 'Standard CRM access for new sales team member',
    requester: { name: 'Sarah Chen', avatar: 'SC', department: 'Sales' },
    ticket: { id: 'TKT-1234', subject: 'Need access to Salesforce' },
    workflow: 'Standard App Access',
    actions: [
      { system: 'Okta', action: 'Add to Salesforce-Users group' },
      { system: 'Salesforce', action: 'Create user with Sales profile' }
    ],
    risk: 'low',
    requestedAt: '2024-01-15T10:31:00Z',
    deadline: '2024-01-15T18:31:00Z',
    status: 'pending',
    approvers: [{ name: 'Mike Torres', role: 'Manager', status: 'pending' }],
    policyConstraints: [
      { policy: 'App Access Policy', rule: 'Manager approval required for SaaS applications', policyId: 'POL-101' }
    ],
    riskExplanation: 'Standard access grant with no sensitive data exposure'
  },
  {
    id: 'APR-002',
    type: 'elevated_access',
    title: 'AWS Admin Access for Alex Kim',
    description: 'Temporary admin access for production debugging',
    requester: { name: 'Alex Kim', avatar: 'AK', department: 'Engineering' },
    ticket: { id: 'TKT-1240', subject: 'Need AWS admin for incident' },
    workflow: 'Elevated Access Request',
    actions: [
      { system: 'AWS IAM', action: 'Grant AdministratorAccess policy' },
      { system: 'Okta', action: 'Add to AWS-Admins group (24h)' }
    ],
    risk: 'high',
    requestedAt: '2024-01-15T09:15:00Z',
    deadline: '2024-01-15T13:15:00Z',
    status: 'pending',
    approvers: [
      { name: 'Jordan Lee', role: 'Manager', status: 'approved' },
      { name: 'Security Team', role: 'Security', status: 'pending' }
    ],
    policyConstraints: [
      { policy: 'Elevated Access Policy', rule: 'Production admin access requires dual approval (manager + security)', policyId: 'POL-200' },
      { policy: 'AWS Security Policy', rule: 'AdministratorAccess grants require security review', policyId: 'POL-201' },
      { policy: 'Audit Policy', rule: 'All elevated access must be time-bound (max 24h)', policyId: 'POL-300' }
    ],
    riskExplanation: 'Grants full AWS account control. User can access all production resources, modify IAM, and incur costs. Time-limited to 24 hours with full audit logging.'
  },
  {
    id: 'APR-003',
    type: 'hardware_request',
    title: 'MacBook Pro for New Hire David Park',
    description: 'Standard engineering laptop for new team member',
    requester: { name: 'Emily Zhang', avatar: 'EZ', department: 'IT' },
    ticket: { id: 'TKT-1236', subject: 'New laptop request for new hire' },
    workflow: 'Hardware Procurement',
    actions: [
      { system: 'Jamf', action: 'Enroll new device' },
      { system: 'Asset DB', action: 'Create asset record' }
    ],
    risk: 'low',
    requestedAt: '2024-01-15T08:45:00Z',
    deadline: '2024-01-16T08:45:00Z',
    status: 'pending',
    approvers: [
      { name: 'Mike Torres', role: 'Manager', status: 'approved' },
      { name: 'Finance Team', role: 'Finance', status: 'pending' }
    ],
    cost: '$2,499',
    policyConstraints: [
      { policy: 'Procurement Policy', rule: 'Hardware >$1,000 requires manager + finance approval', policyId: 'POL-400' },
      { policy: 'Asset Policy', rule: 'All hardware must be enrolled in MDM before delivery', policyId: 'POL-401' }
    ],
    riskExplanation: 'Standard hardware procurement with budget impact. Device will be MDM-enrolled and tracked.'
  },
  {
    id: 'APR-004',
    type: 'workflow_publish',
    title: 'Publish: Password Reset Workflow v2',
    description: 'Updated workflow with MFA verification step',
    requester: { name: 'Taylor Brown', avatar: 'TB', department: 'IT' },
    ticket: null,
    workflow: 'Workflow Publishing',
    actions: [
      { system: 'Vedaya', action: 'Deploy workflow to production' }
    ],
    risk: 'medium',
    requestedAt: '2024-01-15T11:00:00Z',
    deadline: '2024-01-16T11:00:00Z',
    status: 'pending',
    approvers: [{ name: 'IT Operations', role: 'Team Manager', status: 'pending' }],
    policyConstraints: [
      { policy: 'Change Management Policy', rule: 'Production workflow changes require team manager approval', policyId: 'POL-500' },
      { policy: 'Security Review Policy', rule: 'Workflows handling credentials must pass security review', policyId: 'POL-501' }
    ],
    riskExplanation: 'Workflow modifies user authentication state. Includes MFA verification for additional security. Pre-tested in staging environment.'
  }
];

const mockHistory = [
  {
    id: 'APR-H001',
    title: 'Slack Access for Jordan Lee',
    status: 'approved',
    decidedAt: '2024-01-14T16:30:00Z',
    decidedBy: 'Mike Torres'
  },
  {
    id: 'APR-H002',
    title: 'Production DB Access (Denied)',
    status: 'rejected',
    decidedAt: '2024-01-14T14:15:00Z',
    decidedBy: 'Security Team',
    reason: 'Insufficient justification for production access'
  },
  {
    id: 'APR-H003',
    title: 'GitHub Org Membership',
    status: 'approved',
    decidedAt: '2024-01-14T10:00:00Z',
    decidedBy: 'Emily Zhang'
  }
];

const riskConfig = {
  low: { label: 'Low Risk', color: 'bg-green-100 text-green-800 border-green-200' },
  medium: { label: 'Medium Risk', color: 'bg-amber-100 text-amber-800 border-amber-200' },
  high: { label: 'High Risk', color: 'bg-red-100 text-red-800 border-red-200' }
};

const typeConfig = {
  access_request: { label: 'Access Request', icon: User },
  elevated_access: { label: 'Elevated Access', icon: Shield },
  hardware_request: { label: 'Hardware', icon: FileText },
  workflow_publish: { label: 'Workflow Publish', icon: Zap }
};

export const Approvals = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRisk, setSelectedRisk] = useState('all');
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [activeTab, setActiveTab] = useState('pending');

  // Demo store integration
  const [demoApprovals, setDemoApprovals] = useState(listApprovals());
  const [aha, setAha] = useState(isAhaComplete());
  const [showAdvanced, setShowAdvanced] = useState(() => {
    try {
      return localStorage.getItem('vedaya_approvals_advanced') === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    return subscribe(() => {
      setDemoApprovals(listApprovals());
      setAha(isAhaComplete());
    });
  }, []);

  const advanced = aha && showAdvanced;

  const toggleAdvanced = () => {
    const next = !showAdvanced;
    try {
      localStorage.setItem('vedaya_approvals_advanced', next ? 'true' : 'false');
    } catch {}
    setShowAdvanced(next);
  };

  // Combine demo approvals with mock for display
  const allApprovals = useMemo(() => {
    const demoItems = demoApprovals.map(a => ({
      ...a,
      type: 'access_request',
      requester: a.requester || { name: 'User', avatar: 'U', department: 'Unknown' },
      ticket: a.ticketId ? { id: a.ticketId, subject: a.description } : null,
      workflow: 'Demo Workflow',
      deadline: new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString(),
      approvers: [{ name: 'Manager', role: 'Manager', status: 'pending' }],
      policyConstraints: [],
    }));
    return [...demoItems, ...mockApprovals];
  }, [demoApprovals]);

  const pendingApprovals = useMemo(() => allApprovals.filter(a => a.status === 'pending'), [allApprovals]);
  const filteredApprovals = useMemo(() => {
    return pendingApprovals.filter(approval => {
      const matchesSearch = approval.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        approval.requester.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesRisk = selectedRisk === 'all' || approval.risk === selectedRisk;
      return matchesSearch && matchesRisk;
    });
  }, [searchQuery, selectedRisk, pendingApprovals]);

  const stats = {
    pending: pendingApprovals.length,
    urgent: pendingApprovals.filter(a => {
      const deadline = new Date(a.deadline);
      const now = new Date();
      return (deadline - now) < 4 * 60 * 60 * 1000;
    }).length,
    highRisk: pendingApprovals.filter(a => a.risk === 'high').length
  };

  const handleApprove = async () => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    // If it's a demo store approval, use approveAndExecute to update both approval and ticket
    if (selectedApproval?.id && demoApprovals.find(a => a.id === selectedApproval.id)) {
      approveAndExecute(selectedApproval.id);
    }
    toast.success('Approved and executed!', {
      description: `${selectedApproval?.title} - Actions have been executed.`
    });
    setShowApproveDialog(false);
    setSelectedApproval(null);
  };

  const handleReject = async () => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    // If it's a demo store approval, use rejectApproval to update both approval and ticket
    if (selectedApproval?.id && demoApprovals.find(a => a.id === selectedApproval.id)) {
      rejectApproval(selectedApproval.id, rejectReason || 'Request denied');
    }
    toast.success('Request rejected', {
      description: `${selectedApproval?.title} has been rejected.`
    });
    setShowRejectDialog(false);
    setRejectReason('');
    setSelectedApproval(null);
  };

  // Quick approve/reject for compact mode - uses demoStore functions that update both approval and ticket
  const handleQuickApprove = async (approval) => {
    if (approval?.id && demoApprovals.find(a => a.id === approval.id)) {
      approveAndExecute(approval.id);
      toast.success('Approved and executed!', {
        description: `${approval.title} - Actions have been executed.`,
      });
    }
  };

  const handleQuickReject = async (approval) => {
    if (approval?.id && demoApprovals.find(a => a.id === approval.id)) {
      rejectApproval(approval.id, 'Request denied by manager');
      toast.success('Rejected', { description: approval.title });
    }
  };

  const getTimeUntilDeadline = (deadline) => {
    const now = new Date();
    const dl = new Date(deadline);
    const diff = dl - now;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 24) return `${Math.floor(hours / 24)}d remaining`;
    if (hours > 0) return `${hours}h ${mins}m remaining`;
    return `${mins}m remaining`;
  };

  // Compact mode UI (pre-advanced) - only show demo approvals
  const compactApprovals = demoApprovals.filter(a => a.status === 'pending');

  if (!advanced) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-foreground flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-muted-foreground" />
              Approvals
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {compactApprovals.length} pending {compactApprovals.length === 1 ? 'approval' : 'approvals'}
            </p>
          </div>
          {aha && (
            <Button variant="outline" size="sm" onClick={toggleAdvanced}>
              <Eye className="w-4 h-4 mr-2" />
              Show advanced view
            </Button>
          )}
        </div>

        {compactApprovals.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No pending approvals</h3>
              <p className="text-sm text-muted-foreground">You're all caught up!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {compactApprovals.map((approval) => {
              const risk = riskConfig[approval.risk] || riskConfig.low;
              return (
                <Card key={approval.id} className="hover:border-primary/50 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-xs font-medium shrink-0">
                          {approval.requester?.avatar || 'U'}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-mono text-muted-foreground">{approval.id}</span>
                            <Badge variant="outline" className={cn("text-xs", risk.color)}>
                              {risk.label}
                            </Badge>
                          </div>
                          <h3 className="text-sm font-medium text-foreground truncate">{approval.title}</h3>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {approval.requester?.name || 'Unknown'} · {approval.description?.slice(0, 50)}...
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button variant="outline" size="sm" onClick={() => handleQuickReject(approval)}>
                          <XCircle className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                        <Button size="sm" onClick={() => handleQuickApprove(approval)}>
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background">
      <div className="border-b bg-card">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">Approvals</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Review and approve pending requests
              </p>
            </div>
            {aha && (
              <Button variant="ghost" size="sm" onClick={toggleAdvanced}>
                <Eye className="w-4 h-4 mr-2" />
                Hide advanced
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid grid-cols-3 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.pending}</p>
                  <p className="text-xs text-muted-foreground">Pending Approvals</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={stats.urgent > 0 ? "border-amber-200" : ""}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.urgent}</p>
                  <p className="text-xs text-muted-foreground">Urgent ({"<"}4h)</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={stats.highRisk > 0 ? "border-red-200" : ""}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.highRisk}</p>
                  <p className="text-xs text-muted-foreground">High Risk</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="flex items-center justify-between">
            <TabsList>
              <TabsTrigger value="pending">
                Pending
                {stats.pending > 0 && (
                  <Badge variant="secondary" className="ml-2">{stats.pending}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="history">History</TabsTrigger>
            </TabsList>

            {activeTab === 'pending' && (
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search approvals..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 w-64"
                  />
                </div>
                <Select value={selectedRisk} onValueChange={setSelectedRisk}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Risks</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <TabsContent value="pending" className="space-y-4">
            {filteredApprovals.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">All caught up!</h3>
                  <p className="text-sm text-muted-foreground">No pending approvals match your filters</p>
                </CardContent>
              </Card>
            ) : (
              filteredApprovals.map((approval) => {
                const risk = riskConfig[approval.risk];
                const type = typeConfig[approval.type];
                const TypeIcon = type.icon;
                const isUrgent = (new Date(approval.deadline) - new Date()) < 4 * 60 * 60 * 1000;

                return (
                  <Card
                    key={approval.id}
                    className={cn(
                      "cursor-pointer hover:border-primary/50 transition-colors",
                      isUrgent && "border-amber-200"
                    )}
                    onClick={() => navigate(`/approvals/${approval.id}`)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/approvals/${approval.id}`);
                      }
                    }}
                    aria-label={`View approval ${approval.id}: ${approval.title}`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-xs font-medium shrink-0">
                          {approval.requester.avatar}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-xs">
                              <TypeIcon className="w-3 h-3 mr-1" />
                              {type.label}
                            </Badge>
                            <Badge variant="outline" className={cn("text-xs", risk.color)}>
                              {risk.label}
                            </Badge>
                            {isUrgent && (
                              <Badge variant="destructive" className="text-xs">Urgent</Badge>
                            )}
                          </div>
                          <h3 className="text-base font-semibold text-foreground">{approval.title}</h3>
                          <p className="text-sm text-muted-foreground mt-0.5">{approval.description}</p>

                          <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {approval.requester.name} ({approval.requester.department})
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {getTimeUntilDeadline(approval.deadline)}
                            </span>
                            {approval.ticket && (
                              <Link
                                to={`/tickets/${approval.ticket.id}`}
                                className="flex items-center gap-1 text-primary hover:underline"
                              >
                                <ExternalLink className="w-3 h-3" />
                                {approval.ticket.id}
                              </Link>
                            )}
                            {approval.cost && (
                              <span className="font-medium text-foreground">{approval.cost}</span>
                            )}
                          </div>

                          <div className="mt-3 grid grid-cols-2 gap-3">
                            <div className="p-3 bg-muted/50 rounded-lg">
                              <p className="text-xs font-medium text-foreground mb-2 flex items-center gap-1">
                                <Zap className="w-3 h-3" />
                                Actions to be taken
                              </p>
                              <div className="space-y-1">
                                {approval.actions.map((action, idx) => (
                                  <div key={idx} className="flex items-center gap-2 text-xs">
                                    <Badge variant="secondary" className="text-[10px]">{action.system}</Badge>
                                    <span className="text-muted-foreground">{action.action}</span>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {approval.policyConstraints && approval.policyConstraints.length > 0 && (
                              <div className="p-3 bg-blue-50/50 border border-blue-100 rounded-lg">
                                <p className="text-xs font-medium text-blue-900 mb-2 flex items-center gap-1">
                                  <Scale className="w-3 h-3" />
                                  Why approval is required
                                </p>
                                <div className="space-y-1.5">
                                  {approval.policyConstraints.slice(0, 2).map((constraint, idx) => (
                                    <div key={idx} className="text-xs">
                                      <Link
                                        to={`/policy-studio/${constraint.policyId}`}
                                        className="font-medium text-blue-700 hover:underline"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        {constraint.policy}
                                      </Link>
                                      <p className="text-blue-600/80 text-[11px]">{constraint.rule}</p>
                                    </div>
                                  ))}
                                  {approval.policyConstraints.length > 2 && (
                                    <p className="text-xs text-blue-600">+{approval.policyConstraints.length - 2} more policies</p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>

                          {approval.riskExplanation && approval.risk !== 'low' && (
                            <div className={cn(
                              "mt-3 p-3 rounded-lg border flex items-start gap-2",
                              approval.risk === 'high' && "bg-red-50/50 border-red-200",
                              approval.risk === 'medium' && "bg-amber-50/50 border-amber-200"
                            )}>
                              <AlertCircle className={cn(
                                "w-4 h-4 shrink-0 mt-0.5",
                                approval.risk === 'high' && "text-red-600",
                                approval.risk === 'medium' && "text-amber-600"
                              )} />
                              <div>
                                <p className={cn(
                                  "text-xs font-medium mb-0.5",
                                  approval.risk === 'high' && "text-red-800",
                                  approval.risk === 'medium' && "text-amber-800"
                                )}>
                                  Risk Assessment
                                </p>
                                <p className={cn(
                                  "text-xs",
                                  approval.risk === 'high' && "text-red-700",
                                  approval.risk === 'medium' && "text-amber-700"
                                )}>
                                  {approval.riskExplanation}
                                </p>
                              </div>
                            </div>
                          )}

                          <div className="mt-3 flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">Approval chain:</span>
                            {approval.approvers.map((approver, idx) => (
                              <Badge
                                key={idx}
                                variant="outline"
                                className={cn(
                                  "text-xs",
                                  approver.status === 'approved' && "bg-green-100 text-green-800 border-green-200",
                                  approver.status === 'pending' && "bg-amber-100 text-amber-800 border-amber-200"
                                )}
                              >
                                {approver.status === 'approved' && <CheckCircle className="w-3 h-3 mr-1" />}
                                {approver.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                                {approver.name} ({approver.role})
                              </Badge>
                            ))}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          <Button
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); setSelectedApproval(approval); setShowRejectDialog(true); }}
                          >
                            <XCircle className="w-4 h-4 mr-2" aria-hidden="true" />
                            Reject
                          </Button>
                          <Button
                            onClick={(e) => { e.stopPropagation(); setSelectedApproval(approval); setShowApproveDialog(true); }}
                          >
                            <CheckCircle className="w-4 h-4 mr-2" aria-hidden="true" />
                            Approve
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </TabsContent>

          <TabsContent value="history">
            <Card>
              <CardContent className="p-0">
                <div className="divide-y">
                  {mockHistory.map((item) => (
                    <div key={item.id} className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center",
                          item.status === 'approved' ? "bg-green-100" : "bg-red-100"
                        )}>
                          {item.status === 'approved'
                            ? <CheckCircle className="w-4 h-4 text-green-600" />
                            : <XCircle className="w-4 h-4 text-red-600" />
                          }
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{item.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {item.status === 'approved' ? 'Approved' : 'Rejected'} by {item.decidedBy}
                          </p>
                          {item.reason && (
                            <p className="text-xs text-red-600 mt-1">Reason: {item.reason}</p>
                          )}
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {new Date(item.decidedAt).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Confirm Approval
            </DialogTitle>
            <DialogDescription>
              You're about to approve: <strong>{selectedApproval?.title}</strong>
            </DialogDescription>
          </DialogHeader>
          {selectedApproval && (
            <div className="py-4 space-y-4">
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm font-medium text-foreground mb-2">The following actions will be executed:</p>
                {selectedApproval.actions.map((action, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <ArrowRight className="w-4 h-4" />
                    <span className="font-medium">{action.system}:</span>
                    <span>{action.action}</span>
                  </div>
                ))}
              </div>
              {selectedApproval.risk === 'high' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">
                    <strong>High Risk:</strong> This approval grants elevated privileges. Please verify the request is legitimate.
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveDialog(false)}>Cancel</Button>
            <AsyncButton
              onClick={handleApprove}
              successMessage="Request approved successfully"
              loadingText="Approving..."
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Confirm Approval
            </AsyncButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-500" />
              Reject Request
            </DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting: <strong>{selectedApproval?.title}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Enter rejection reason..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>Cancel</Button>
            <AsyncButton
              variant="destructive"
              onClick={handleReject}
              disabled={!rejectReason.trim()}
              successMessage="Request rejected"
              loadingText="Rejecting..."
            >
              <XCircle className="w-4 h-4 mr-2" />
              Reject Request
            </AsyncButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Approvals;
